import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TenantToolbar from '../components/TenantComponents/TenantToolbar';
import TenantList from '../components/TenantComponents/TenantList';
import TenantIndex from '../components/TenantComponents/TenantIndex';
import useTenantRegistryStore from '../store/useTenantRegistryStore';
import {
  sortAndGroupTenants,
  TenantGrouping,
  TenantListSortOption,
} from '../utils/TenantUtils';
import { useInteractionTimeout } from '../hooks/useInteractionTimeout';
import { PageHeader } from '../components/PageHeader';
import { CompaniesModal } from '../components/Modals/CompanyModal';
import { Company } from '../types/company';
import { useTenants } from '../hooks/useTenants';
import { TenantWithFloor } from '../types/unit';
import { useConfig } from '../providers/ConfigProvider';
import { useNavigateToDefaultView } from '../hooks/useNavigateToDefaultView';

interface Props {
  portrait?: boolean;
}
const TenantRegistryView: FC<Props> = ({ portrait }) => {
  const { tenants, demographic, tenantLinkProps } = useTenants();
  const { sortOption, setSort, sortDirection, setSortDirection } =
    useTenantRegistryStore();
  const listRef = useRef<HTMLDivElement>(null);
  const indexRef = useRef<HTMLDivElement>(null);
  const {
    defaultTenantListSortOrder,
    defaultTenantListSortOption,
    interactionTimeout,
    tenantListColumns,
  } = useConfig();
  const navigateToDefaultView = useNavigateToDefaultView([listRef, indexRef]);

  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [listIsScrollable, setListIsScrollable] = useState(false);

  const resetView = useCallback(() => {
    if (!sortOption || !sortDirection) {
      return;
    }
    setSort(defaultTenantListSortOption, defaultTenantListSortOrder);
    setSelectedCompany(null);
  }, [
    sortOption,
    sortDirection,
    setSort,
    defaultTenantListSortOption,
    defaultTenantListSortOrder,
  ]);

  const onInteractionTimeout = useCallback(() => {
    resetView();
    navigateToDefaultView();
  }, [navigateToDefaultView, resetView]);

  useInteractionTimeout(interactionTimeout, onInteractionTimeout);

  useEffect(() => {
    setSort(defaultTenantListSortOption, defaultTenantListSortOrder);
    return () => {
      //Set default sort on unmount
      setSort(defaultTenantListSortOption, defaultTenantListSortOrder);
    };
  }, [defaultTenantListSortOption, defaultTenantListSortOrder, setSort]);

  const [scrollTo, setScrollTo] = useState('');

  const onSort = useCallback(
    (sortBy: TenantListSortOption) => {
      if (sortOption === sortBy) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        return;
      }
      setSort(
        sortBy,
        sortBy === defaultTenantListSortOption
          ? defaultTenantListSortOrder
          : 'asc',
      );
      setScrollTo('top');
    },
    [
      sortOption,
      setSort,
      defaultTenantListSortOption,
      defaultTenantListSortOrder,
      setSortDirection,
      sortDirection,
    ],
  );

  // Will trigger on refetching tenants and sorting
  const sortedTenants = useMemo<TenantGrouping[]>(
    () => sortAndGroupTenants(tenants, sortOption, sortDirection),
    [tenants, sortOption, sortDirection],
  );

  const onListItemClick = useCallback((tenant: TenantWithFloor) => {
    if (tenant.type === 'individual') {
      return;
    }

    setSelectedCompany(tenant.company as Company);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      const el = document.getElementById('tenantList');
      if (!el) {
        return;
      }

      setListIsScrollable(el.scrollHeight > el.clientHeight);
    }, 1);
  }, [onSort]);

  return (
    <div className="flex grow flex-col overflow-hidden">
      <PageHeader
        icon={tenantLinkProps.icon}
        title={tenantLinkProps.label}
        slim={portrait}
      />
      <TenantToolbar
        onSortClick={onSort}
        sort={sortOption}
        sortDirection={sortDirection}
        onlyCompanies={demographic === 'companies'}
      />
      <div className="flex grow overflow-auto">
        <TenantList
          tenantGroups={sortedTenants}
          scrollTo={scrollTo}
          sort={sortOption}
          ref={listRef}
          onListItemClick={onListItemClick}
          addRightPadding={!listIsScrollable}
          columns={tenantListColumns}
        />
        {listIsScrollable ? (
          <TenantIndex
            indices={sortedTenants.map((group) => group.group)}
            onClick={(index) => setScrollTo(index)}
            className="px-2"
            mainRef={indexRef}
          />
        ) : null}
      </div>
      <CompaniesModal
        show={!!selectedCompany}
        companies={selectedCompany ? [selectedCompany] : []}
        onClose={() => setSelectedCompany(null)}
      />
    </div>
  );
};

export default TenantRegistryView;
